import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  VALID_COMPOSITE_MISSING_MODES,
  VALID_COMPOSITE_OPERATORS,
  VALID_NUMERIC_OPERATORS,
  VALID_RATE_DIRECTIONS,
  type RuleType,
} from "@/data/alarmRules/engineConstants";
import type { AlarmRule, ExpressionDatapointSpec } from "@/data/alarmRules/wireFormat";
import { NumberField } from "@/components/ui/NumberField";
import { useT } from "@/lib/i18n";

import { DurationInput } from "./DurationInput";
import { DatapointPicker } from "./DatapointPicker";
import { WeeklyScheduleGrid, makeEmptyScheduleGrid } from "./WeeklyScheduleGrid";
import { ExpressionEditor } from "./ExpressionEditor";

interface Props {
  type: RuleType;
  deviceId: string | null;
  /**
   * When editing a `composite` rule, the form needs to know which other
   * rules on the same device are valid child references. The parent
   * page passes the loaded envelope through so we can render the list.
   */
  candidateChildren?: { id: string; name: string }[];
}

/**
 * Renders the per-type config fields. Switches on `type` and feeds the
 * shared react-hook-form context with `setValue("config.<field>", ...)`
 * style updates so the discriminated union stays consistent.
 */
export function RuleTypeFields({ type, deviceId, candidateChildren }: Props) {
  switch (type) {
    case "threshold":
      return <ThresholdFields />;
    case "range":
      return <RangeFields />;
    case "boolean":
      return <BooleanFields />;
    case "inactivity":
      return <InactivityFields />;
    case "stuck":
      return <StuckFields />;
    case "rate_of_change":
      return <RateFields />;
    case "delta":
      return <DeltaFields />;
    case "daily_energy":
      return <DailyEnergyFields />;
    case "excessive_cycling":
      return <ExcessiveCyclingFields />;
    case "peak_demand":
      return <PeakDemandFields />;
    case "idle_consumption":
      return <IdleConsumptionFields />;
    case "expression":
      return <ExpressionFields deviceId={deviceId} />;
    case "composite":
      return <CompositeFields candidateChildren={candidateChildren ?? []} />;
    case "comm_degradation":
      return <CommDegradationFields />;
    default:
      return null;
  }
}

/**
 * i18n-aware field wrapper. `label`/`hint` are DICT KEYS, translated at
 * render time so every per-type field is localized without threading a
 * `t` through 14 callers. Wire enum `<option>` VALUES stay raw English.
 */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  const t = useT();
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-text-faint">{t(label)}</span>
      {children}
      {hint && <span className="text-[10px] text-text-faint">{t(hint)}</span>}
    </label>
  );
}

function ThresholdFields() {
  const { register, control } = useFormContext<AlarmRule>();
  const t = useT();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.operator">
        <select className="form-input" {...register("config.operator" as const)}>
          {VALID_NUMERIC_OPERATORS.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
      </Field>
      <Field label="manage.rules.field.threshold">
        <Controller
          control={control}
          name={"config.threshold" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              allowSigned
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.hysteresis" hint="manage.rules.field.hysteresisHint">
        <Controller
          control={control}
          name={"config.hysteresis" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.holdDuration" hint="manage.rules.field.holdDurationHint">
        <Controller
          control={control}
          name={"config.duration_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
      <Field label="manage.rules.field.unitDisplay">
        <input
          type="text"
          className="form-input"
          placeholder={t("manage.rules.field.unitPlaceholder")}
          {...register("config.unit" as const)}
        />
      </Field>
    </div>
  );
}

function RangeFields() {
  const { register, control } = useFormContext<AlarmRule>();
  const t = useT();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.low">
        <Controller
          control={control}
          name={"config.lo" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              allowSigned
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.high">
        <Controller
          control={control}
          name={"config.hi" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              allowSigned
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.insideIsAlarm">
        <select className="form-input" {...register("config.inside_is_alarm" as const)}>
          <option value="false">{t("manage.rules.field.alarmOutside")}</option>
          <option value="true">{t("manage.rules.field.alarmInside")}</option>
        </select>
      </Field>
      <Field label="manage.rules.field.hysteresis">
        <Controller
          control={control}
          name={"config.hysteresis" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.holdDuration">
        <Controller
          control={control}
          name={"config.duration_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
    </div>
  );
}

function BooleanFields() {
  const { register, control } = useFormContext<AlarmRule>();
  const t = useT();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.alarmWhenValue">
        <select className="form-input" {...register("config.alarm_when" as const)}>
          <option value="true">{t("manage.rules.field.boolTrue")}</option>
          <option value="false">{t("manage.rules.field.boolFalse")}</option>
        </select>
      </Field>
      <Field label="manage.rules.field.holdDuration">
        <Controller
          control={control}
          name={"config.duration_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
    </div>
  );
}

function InactivityFields() {
  const { control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.inactivityTimeout" hint="manage.rules.field.inactivityTimeoutHint">
        <Controller
          control={control}
          name={"config.timeout_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
    </div>
  );
}

function StuckFields() {
  const { control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.tolerance" hint="manage.rules.field.toleranceHint">
        <Controller
          control={control}
          name={"config.tolerance" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.window">
        <Controller
          control={control}
          name={"config.window_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
    </div>
  );
}

function RateFields() {
  const { register, control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.maxRate">
        <Controller
          control={control}
          name={"config.max_rate" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.window">
        <Controller
          control={control}
          name={"config.window_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
      <Field label="manage.rules.field.direction">
        <select className="form-input" {...register("config.direction" as const)}>
          {VALID_RATE_DIRECTIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

function DeltaFields() {
  const { control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.maxDelta">
        <Controller
          control={control}
          name={"config.max_delta" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.window">
        <Controller
          control={control}
          name={"config.window_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
    </div>
  );
}

function DailyEnergyFields() {
  const { register, control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.operator">
        <select className="form-input" {...register("config.operator" as const)}>
          {VALID_NUMERIC_OPERATORS.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
      </Field>
      <Field label="manage.rules.field.thresholdKwh">
        <Controller
          control={control}
          name={"config.threshold" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              allowSigned
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.timeframe" hint="manage.rules.field.timeframeHintEnergy">
        <input type="text" className="form-input" {...register("config.timeframe" as const)} />
      </Field>
    </div>
  );
}

function ExcessiveCyclingFields() {
  const { register, control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.maxCycles">
        <Controller
          control={control}
          name={"config.max_cycles" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={(v) => field.onChange(Math.round(v))}
              min={0}
              step={1}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.timeframe" hint="manage.rules.field.timeframeHintCycling">
        <input type="text" className="form-input" {...register("config.timeframe" as const)} />
      </Field>
      <Field label="manage.rules.field.powerThreshold" hint="manage.rules.field.powerThresholdHintCycling">
        <Controller
          control={control}
          name={"config.power_threshold" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
    </div>
  );
}

function PeakDemandFields() {
  const { control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.demandLimit">
        <Controller
          control={control}
          name={"config.demand_limit" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.warningPercent" hint="manage.rules.field.warningPercentHint">
        <Controller
          control={control}
          name={"config.warning_percent" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              max={100}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.holdDuration">
        <Controller
          control={control}
          name={"config.duration_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
    </div>
  );
}

function IdleConsumptionFields() {
  const { control } = useFormContext<AlarmRule>();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="manage.rules.field.powerThresholdW">
          <Controller
            control={control}
            name={"config.power_threshold" as const}
            render={({ field }) => (
              <NumberField
                value={(field.value as number) ?? 0}
                onChange={field.onChange}
                allowSigned
                className="form-input"
              />
            )}
          />
        </Field>
        <Field label="manage.rules.field.holdDuration">
          <Controller
            control={control}
            name={"config.duration_s" as const}
            render={({ field }) => (
              <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
            )}
          />
        </Field>
      </div>
      <Field label="manage.rules.field.operatingHours">
        <Controller
          control={control}
          name={"config.weeklySchedule" as const}
          render={({ field }) => (
            <WeeklyScheduleGrid
              value={(field.value as boolean[][]) ?? makeEmptyScheduleGrid()}
              onChange={field.onChange}
            />
          )}
        />
      </Field>
    </div>
  );
}

function ExpressionFields({ deviceId }: { deviceId: string | null }) {
  const { control } = useFormContext<AlarmRule>();
  return (
    <div className="space-y-3">
      <Controller
        control={control}
        name={"config" as const}
        render={({ field }) => {
          const cfg = (field.value ?? {}) as {
            expression?: string;
            datapoints?: Record<string, ExpressionDatapointSpec>;
            duration_s?: number;
          };
          return (
            <>
              <ExpressionEditor
                expression={cfg.expression ?? ""}
                onExpressionChange={(next) => field.onChange({ ...cfg, expression: next })}
                datapoints={cfg.datapoints ?? {}}
                onDatapointsChange={(next) =>
                  field.onChange({ ...cfg, datapoints: next })
                }
                deviceId={deviceId}
              />
              <Field label="manage.rules.field.holdDuration">
                <DurationInput
                  value={cfg.duration_s ?? 0}
                  onChange={(secs) => field.onChange({ ...cfg, duration_s: secs })}
                />
              </Field>
            </>
          );
        }}
      />
    </div>
  );
}

function CompositeFields({ candidateChildren }: { candidateChildren: { id: string; name: string }[] }) {
  const { register, control } = useFormContext<AlarmRule>();
  const t = useT();
  const selected = (useWatch({ control, name: "config.child_rule_ids" as const }) ?? []) as string[];
  const setSelected = (next: string[]) => {
    const event = { target: { value: next } } as unknown as React.ChangeEvent<HTMLInputElement>;
    // react-hook-form's setValue isn't directly exposed via register; use Controller below instead.
    void event;
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="manage.rules.field.combineUsing">
          <select className="form-input" {...register("config.operator" as const)}>
            {VALID_COMPOSITE_OPERATORS.map((o) => (
              <option key={o} value={o}>
                {o.toUpperCase()}
              </option>
            ))}
          </select>
        </Field>
        <Field label="manage.rules.field.missingChildren">
          <select className="form-input" {...register("config.treat_missing_as" as const)}>
            {VALID_COMPOSITE_MISSING_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="manage.rules.field.holdDuration">
          <Controller
            control={control}
            name={"config.duration_s" as const}
            render={({ field }) => (
              <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
            )}
          />
        </Field>
      </div>

      <Field label="manage.rules.field.childRules">
        <Controller
          control={control}
          name={"config.child_rule_ids" as const}
          render={({ field }) => {
            const value = (field.value as string[]) ?? [];
            return (
              <div className="border border-edge rounded p-2 space-y-1 max-h-48 overflow-auto">
                {candidateChildren.length === 0 ? (
                  <div className="text-[11px] text-text-faint">
                    {t("manage.rules.field.noOtherRules")}
                  </div>
                ) : (
                  candidateChildren.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-[12px]">
                      <input
                        type="checkbox"
                        checked={value.includes(c.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            field.onChange([...value, c.id]);
                          } else {
                            field.onChange(value.filter((x) => x !== c.id));
                          }
                        }}
                      />
                      <span className="font-mono text-[11px] text-text-faint">
                        {c.id}
                      </span>
                      <span>{c.name}</span>
                    </label>
                  ))
                )}
                {value.length < 2 && (
                  <div className="text-[11px] text-warn">
                    {t("manage.rules.field.pickTwoChildren")}
                  </div>
                )}
              </div>
            );
          }}
        />
        {/* setSelected is wired through Controller above. */}
        <span className="sr-only">{selected.join(",")}</span>
        <span onClick={() => setSelected(selected)} className="hidden" />
      </Field>
    </div>
  );
}

function CommDegradationFields() {
  const { control } = useFormContext<AlarmRule>();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="manage.rules.field.expectedRate">
        <Controller
          control={control}
          name={"config.expected_rate_hz" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.window">
        <Controller
          control={control}
          name={"config.window_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
      <Field label="manage.rules.field.lossThreshold" hint="manage.rules.field.lossThresholdHint">
        <Controller
          control={control}
          name={"config.loss_pct_threshold" as const}
          render={({ field }) => (
            <NumberField
              value={(field.value as number) ?? 0}
              onChange={field.onChange}
              min={0}
              max={100}
              className="form-input"
            />
          )}
        />
      </Field>
      <Field label="manage.rules.field.holdDuration">
        <Controller
          control={control}
          name={"config.duration_s" as const}
          render={({ field }) => (
            <DurationInput value={field.value as number ?? 0} onChange={field.onChange} />
          )}
        />
      </Field>
    </div>
  );
}
